import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useProfile } from '../context/ProfileContext'
import {
  User,
  SignOut,
  Buildings,
  Globe,
  Moon,
  Sun,
  CaretDown
} from '@phosphor-icons/react'

export function UserMenu() {
  const { user, logout } = useAuth()
  const { currentProfile } = useProfile()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [theme, setTheme] = useState<'light' | 'dark'>('light')
  const [language, setLanguage] = useState<'pt' | 'en' | 'es'>('pt')
  const menuRef = useRef<HTMLDivElement>(null)

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    // TODO: Implement theme switching logic
    console.log('Theme changed to:', newTheme)
  }

  const changeLanguage = (lang: 'pt' | 'en' | 'es') => {
    setLanguage(lang)
    // TODO: Implement i18n logic
    console.log('Language changed to:', lang)
    setIsOpen(false)
  }

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase()
  }

  const languageLabels = {
    pt: 'Português',
    en: 'English',
    es: 'Español'
  }

  if (!user) return null

  return (
    <div className="relative" ref={menuRef}>
      {/* User Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      >
        {/* User Avatar */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white font-semibold shadow-md">
          {user.avatar_url ? (
            <img
              src={user.avatar_url}
              alt={user.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            <span className="text-sm">{getInitials(user.name || 'U')}</span>
          )}
        </div>

        {/* User Info */}
        <div className="hidden md:block text-left">
          <p className="text-sm font-semibold text-gray-900">{user.name}</p>
          <p className="text-xs text-gray-500">{user.email}</p>
        </div>

        {/* Dropdown Icon */}
        <CaretDown
          size={16}
          className={`text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
          {/* User Info Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <p className="text-sm font-semibold text-gray-900">{user.name}</p>
            <p className="text-xs text-gray-500">{user.email}</p>
            {user.current_company && (
              <div className="mt-2 px-2 py-1 bg-emerald-50 rounded text-xs font-medium text-emerald-700">
                {user.current_company.name}
              </div>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-2">
            {/* Profile */}
            <button
              onClick={() => {
                const settingsPath = currentProfile === 'master'
                  ? '/master/settings'
                  : `/${currentProfile}/settings`
                navigate(settingsPath)
                setIsOpen(false)
              }}
              className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
            >
              <User size={18} className="text-gray-600" />
              <span className="text-sm text-gray-700">Meu Perfil</span>
            </button>

            {/* Switch Company */}
            {user.companies && user.companies.length > 1 && (
              <button
                onClick={() => {
                  // TODO: Open company switcher modal
                  console.log('Open company switcher')
                  setIsOpen(false)
                }}
                className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
              >
                <Buildings size={18} className="text-gray-600" />
                <div className="flex-1">
                  <span className="text-sm text-gray-700">Trocar Empresa</span>
                  <p className="text-xs text-gray-400">{user.companies.length} empresas</p>
                </div>
              </button>
            )}

            <div className="my-2 border-t border-gray-100" />

            {/* Language */}
            <div className="px-4 py-2">
              <div className="flex items-center gap-3 mb-2">
                <Globe size={18} className="text-gray-600" />
                <span className="text-sm text-gray-700 font-medium">Idioma</span>
              </div>
              <div className="flex gap-2 ml-7">
                {(['pt', 'en', 'es'] as const).map((lang) => (
                  <button
                    key={lang}
                    onClick={() => changeLanguage(lang)}
                    className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                      language === lang
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {languageLabels[lang]}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="w-full px-4 py-2 flex items-center gap-3 hover:bg-gray-50 transition-colors text-left"
            >
              {theme === 'light' ? (
                <Moon size={18} className="text-gray-600" />
              ) : (
                <Sun size={18} className="text-gray-600" />
              )}
              <span className="text-sm text-gray-700">
                {theme === 'light' ? 'Tema Escuro' : 'Tema Claro'}
              </span>
            </button>

            <div className="my-2 border-t border-gray-100" />

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="w-full px-4 py-2 flex items-center gap-3 hover:bg-red-50 transition-colors text-left group"
            >
              <SignOut size={18} className="text-gray-600 group-hover:text-red-600" />
              <span className="text-sm text-gray-700 group-hover:text-red-700">Sair</span>
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
